import SiteHeader from "@/components/SiteHeader";
import { Download, Quote } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LOGO_LIGHT, LOGO_DARK } from "@/lib/brand";

const LOGO_URL = LOGO_DARK;
const LOGO_LIGHT_URL = LOGO_LIGHT;
const PORTRAIT_1 =
  "https://customer-assets.emergentagent.com/job_ae92616c-2c62-480e-bb26-954231470e5d/artifacts/91n23cva_IMG_7955.JPG";
const PORTRAIT_2 =
  "https://customer-assets.emergentagent.com/job_ae92616c-2c62-480e-bb26-954231470e5d/artifacts/r1lkubid_IMG_7953.JPG";
const RESUME_URL =
  "https://customer-assets.emergentagent.com/job_joseph-crm/artifacts/piee4h9t_202605%20Joseph%20Poche%20Resume.pdf";

const shortBio = `Joseph Poche is a Senior Investment and Strategy Executive focused on Private Equity, Direct Equity, Infrastructure and Climate Finance across Sub-Saharan Africa. He has 17+ years of experience, USD 2B+ in transactions advised, and a network spanning 30+ African countries.`;

const longBio = `Joseph Poche is a senior private equity and emerging-markets investment professional with 17+ years across Sub-Saharan Africa, combining direct investment origination, growth capital, infrastructure investing, blended finance, portfolio oversight, value creation and institutional capital mobilization. He has advised on and executed USD 2B+ in investment and financing transactions, with Africa-focused portfolio exposure exceeding USD 1.5B and a network across 30+ African countries.

He is currently Head of Origination — Africa & Emerging Markets at Frontera Capital Group (Abu Dhabi). Prior to Frontera, he served as Senior Project Manager — Africa Investments & Regional Strategy at Mitsui & Co. Middle East Limited (Dubai), where he supported a diversified Africa-focused portfolio exceeding USD 1.5B. Earlier, he founded and led Berkshire Consultancy Limited (Nairobi), a pan-African investment and advisory platform with a 100+ person team.

He holds an MBA in Finance & Investments (Strathmore, ongoing), executive education from INSEAD, and a BA in International Relations from USIU-Africa. He is a CFA Program Level I Candidate. Languages: English (fluent), Kiswahili (fluent), French (intermediate), Spanish (beginner).`;

const boilerplate = `Joseph Poche is a Senior Investment and Strategy Executive in Private Equity, Direct Equity and Infrastructure & Climate Finance with 17+ years focused on Sub-Saharan Africa. Based between Dubai and Nairobi, he has advised on and executed USD 2B+ in investment and financing transactions, with Africa-focused portfolio exposure exceeding USD 1.5B.`;

function DownloadCard({ title, hint, url, filename, testid }) {
  return (
    <a
      href={url}
      download={filename}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="group block bg-white border border-navy/10 hover:border-gold p-5 transition-colors"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-gold-deep">Download</div>
          <div className="font-serif text-lg text-navy mt-1">{title}</div>
          <div className="text-xs text-navy/60 mt-1">{hint}</div>
        </div>
        <Download className="h-5 w-5 text-navy/40 group-hover:text-gold transition-colors" />
      </div>
    </a>
  );
}

export default function PressKit() {
  const [endorsements, setEndorsements] = useState([]);

  useEffect(() => {
    api.get("/public/endorsements").then((r) => setEndorsements(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="bg-cream text-navy min-h-screen" data-testid="press-page">
      <SiteHeader />

      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-16 pb-12">
        <div className="overline text-gold-deep mb-4">Press & Media</div>
        <h1 className="editorial-h1 text-5xl md:text-6xl text-navy">Media kit.</h1>
        <div className="gold-rule mt-6 mb-8" />
        <p className="text-navy/70 max-w-2xl font-light leading-relaxed">
          For press enquiries, panel & speaking invitations, podcast appearances and editorial use —
          you'll find approved bios, headshots, logo files and contact details below. For anything not listed,
          please use the contact form.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-16 grid grid-cols-1 md:grid-cols-2 gap-6">
        <DownloadCard title="Resume / CV (PDF)" hint="Full resume — current as of 2026" url={RESUME_URL} filename="Joseph_Poche_CV.pdf" testid="presskit-cv" />
        <DownloadCard title="Logo — dark on cream (PNG)" hint="Navy + gold wordmark on cream — for light backgrounds" url={LOGO_URL} filename="JosephPoche_Logo_Dark.png" testid="presskit-logo-dark" />
        <DownloadCard title="Logo — light on navy (PNG)" hint="Gold + cream wordmark on navy — for dark backgrounds" url={LOGO_LIGHT_URL} filename="JosephPoche_Logo_Light.png" testid="presskit-logo-light" />
        <DownloadCard title="Headshot — formal (JPG)" hint="Suit, arms crossed — high resolution" url={PORTRAIT_1} filename="JosephPoche_Headshot_1.jpg" testid="presskit-headshot-1" />
        <DownloadCard title="Headshot — editorial (JPG)" hint="Suit, hand on chin — high resolution" url={PORTRAIT_2} filename="JosephPoche_Headshot_2.jpg" testid="presskit-headshot-2" />
      </section>

      <section className="bg-white border-y border-navy/10 py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="overline text-gold-deep mb-4">Short bio · 50 words</div>
          <h2 className="font-serif text-2xl text-navy mb-6">For introductions, panels & headlines</h2>
          <div className="gold-rule mb-6" />
          <p className="text-navy/80 leading-relaxed font-light max-w-3xl">{shortBio}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="overline text-gold-deep mb-4">Long bio · 250 words</div>
          <h2 className="font-serif text-2xl text-navy mb-6">For features & profile pieces</h2>
          <div className="gold-rule mb-6" />
          <div className="text-navy/80 leading-relaxed font-light max-w-3xl space-y-4 whitespace-pre-line">
            {longBio}
          </div>
        </div>
      </section>

      <section className="bg-navy text-cream py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="overline text-gold mb-4">Boilerplate · 1 paragraph</div>
          <h2 className="font-serif text-2xl mb-6">Standard "about" copy</h2>
          <div className="gold-rule mb-6" />
          <p className="text-cream/85 leading-relaxed font-light max-w-3xl italic">"{boilerplate}"</p>
        </div>
      </section>

      {endorsements.length > 0 && (
        <section className="py-16 bg-cream" data-testid="press-endorsements-preview">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <div className="overline text-gold-deep mb-3">What people say</div>
                <h2 className="font-serif text-2xl text-navy">Selected endorsements</h2>
              </div>
              <Link to="/endorsements" data-testid="press-endorsements-link" className="text-xs uppercase tracking-widest text-gold-deep hover:text-navy whitespace-nowrap">
                Read all →
              </Link>
            </div>
            <div className="gold-rule mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {endorsements.map((e) => (
                <div key={e.id} className="relative bg-white border border-navy/10 p-5" data-testid={`press-endorsement-${e.id}`}>
                  <Quote className="absolute -top-3 left-5 bg-cream px-1.5 text-gold h-6 w-6" />
                  <p className="italic font-serif text-navy/85 leading-relaxed">"{e.quote}"</p>
                  <div className="mt-4 pt-3 border-t border-navy/10">
                    <div className="text-sm font-medium text-navy">{e.name}</div>
                    <div className="text-xs text-navy/60">
                      {[e.role, e.organization].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <div className="overline text-gold-deep mb-4">Press contact</div>
            <h2 className="font-serif text-2xl text-navy mb-6">Direct line</h2>
            <div className="gold-rule mb-6" />
            <div className="space-y-3 text-sm text-navy/85">
              <div><span className="overline text-gold-deep mr-2">Email</span> josephpoche13@gmail.com</div>
              <div><span className="overline text-gold-deep mr-2">Mobile</span> +971 52 357 7244</div>
              <div><span className="overline text-gold-deep mr-2">Based</span> Dubai · Nairobi</div>
              <div><span className="overline text-gold-deep mr-2">LinkedIn</span> linkedin.com/in/joseph-poche</div>
            </div>
          </div>
          <div>
            <div className="overline text-gold-deep mb-4">Topics</div>
            <h2 className="font-serif text-2xl text-navy mb-6">Areas of expertise</h2>
            <div className="gold-rule mb-6" />
            <div className="flex flex-wrap gap-2">
              {[
                "Sub-Saharan Africa",
                "Private equity",
                "Infrastructure finance",
                "Climate & energy transition",
                "Blended finance",
                "DFI strategy",
                "Fintech",
                "Digital infrastructure",
                "Emerging markets",
                "Family offices",
              ].map((t) => (
                <span key={t} className="text-xs border border-gold/40 text-gold-deep px-3 py-1 uppercase tracking-wider">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-navy text-cream/70 py-12">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-xs">© {new Date().getFullYear()} Joseph Poche</div>
      </footer>
    </div>
  );
}
